"use client";

import type { CSSProperties, Ref } from "react";
import { cn } from "../../../lib/cn";
import { resolveSpeechBubbleStackedArrowGeometry } from "../../custom/speech-bubble-stacked-arrow";
import type {
	ContentTooltipColorScheme,
	ContentTooltipSide,
} from "./content-tooltip-types";

export interface ContentTooltipArrowFills {
	/** Continues the panel border around the tip. */
	borderFill: string;
	/** Matches the panel face fill. */
	faceFill: string;
}

/**
 * CSS vars for system face/border — light twin + ability dark SoT under `.dark`.
 */
export const CONTENT_TOOLTIP_SYSTEM_ARROW_VAR_CLASS = cn(
	"[--ct-arrow-face:rgba(255,255,255,0.85)]",
	"[--ct-arrow-border:rgba(0,0,0,0.1)]",
	"dark:[--ct-arrow-face:#0f1320]",
	"dark:[--ct-arrow-border:rgba(255,255,255,0.15)]",
);

/**
 * CSS vars for invert face/border — must flip with `.dark` like
 * {@link CONTENT_TOOLTIP_INVERT_SURFACE_CLASS} (`bg-foreground/95` → `dark:bg-background/95`).
 */
export const CONTENT_TOOLTIP_INVERT_ARROW_VAR_CLASS = cn(
	"[--ct-arrow-face:color-mix(in_srgb,var(--foreground)_95%,transparent)]",
	"dark:[--ct-arrow-face:color-mix(in_srgb,var(--background)_95%,transparent)]",
	"[--ct-arrow-border:transparent]",
	"dark:[--ct-arrow-border:rgba(255,255,255,0.1)]",
);

/**
 * Floating UI / shadcn convention: arrow sits on the edge **facing** the
 * reference — the opposite of the floating placement side.
 *
 * @example placement `top` → staticSide `bottom` (tip points down at the trigger)
 */
export function resolveContentTooltipArrowStaticSide(
	placement: string,
): ContentTooltipSide {
	const side = placement.split("-")[0];
	switch (side) {
		case "top":
			return "bottom";
		case "right":
			return "left";
		case "bottom":
			return "top";
		case "left":
			return "right";
		default:
			return "bottom";
	}
}

/**
 * Border + face fills for stacked tooltip arrows (speech-bubble / BlockQuote contract).
 */
export function resolveContentTooltipArrowFills(
	colorScheme: ContentTooltipColorScheme,
): ContentTooltipArrowFills {
	switch (colorScheme) {
		case "dark":
			return {
				borderFill: "rgba(255, 255, 255, 0.15)",
				faceFill: "#0f1320",
			};
		case "light":
			return {
				borderFill: "rgba(0, 0, 0, 0.1)",
				faceFill: "rgba(255, 255, 255, 0.85)",
			};
		case "system":
			// Vars set by CONTENT_TOOLTIP_SYSTEM_ARROW_VAR_CLASS on the arrow root.
			return {
				borderFill: "var(--ct-arrow-border)",
				faceFill: "var(--ct-arrow-face)",
			};
		case "invert":
			// Vars set by CONTENT_TOOLTIP_INVERT_ARROW_VAR_CLASS on the arrow root.
			return {
				borderFill: "var(--ct-arrow-border)",
				faceFill: "var(--ct-arrow-face)",
			};
	}
}

export interface ContentTooltipStackedArrowProps {
	/**
	 * Edge of the floating panel the arrow sits on (Floating UI `staticSide` —
	 * opposite of placement). Tip points outward from this edge toward the reference.
	 */
	staticSide: ContentTooltipSide;
	arrowX: number | null | undefined;
	arrowY: number | null | undefined;
	colorScheme: ContentTooltipColorScheme;
	arrowRef?: Ref<HTMLDivElement | null>;
	/** Scale vs speech-bubble base geometry (tooltips are denser). */
	arrowScale?: number;
}

/**
 * Layered CSS-triangle arrow — outer border tip + inner face tip — so the
 * panel border continues around the arrow (SpeechBubble / BlockQuote technique).
 *
 * Positioning follows Floating UI + shadcn:
 * - `arrow.x` / `arrow.y` are the box origin (half-size already accounted for)
 * - hang fully outside the edge via BlockQuote non-shell `translateAway`, plus
 *   a 2px outward outset so the tip clears the panel border
 */
export function ContentTooltipStackedArrow({
	staticSide,
	arrowX,
	arrowY,
	colorScheme,
	arrowRef,
	arrowScale = 0.72,
}: ContentTooltipStackedArrowProps) {
	const fills = resolveContentTooltipArrowFills(colorScheme);
	const geo = resolveSpeechBubbleStackedArrowGeometry({
		glassBorder: false,
		arrowScale,
	});

	/**
	 * Hang fully outside the panel edge, then nudge 2px further out so the tip
	 * clears the border (SpeechBubble/BlockQuote stem sits on the rim; tooltips
	 * read cleaner with a slight outset).
	 */
	const edgeOutsetPx = 1;
	const isVertical = staticSide === "top" || staticSide === "bottom";
	const pointsOutUp = staticSide === "top";
	const pointsOutLeft = staticSide === "left";

	const boxWidthPx = isVertical
		? geo.outerHalfWidthPx * 2
		: geo.outerHeightPx;
	const boxHeightPx = isVertical
		? geo.outerHeightPx
		: geo.outerHalfWidthPx * 2;

	/**
	 * BlockQuote non-shell `translateAway`, plus {@link edgeOutsetPx} outward.
	 */
	const hangTransform = isVertical
		? pointsOutUp
			? `translateY(calc(-100% - ${edgeOutsetPx}px))`
			: `translateY(calc(100% + ${edgeOutsetPx}px))`
		: pointsOutLeft
			? `translateX(calc(-100% - ${edgeOutsetPx}px))`
			: `translateX(calc(100% + ${edgeOutsetPx}px))`;

	const alignAxisTransform = isVertical
		? arrowX == null
			? "translateX(-50%)"
			: undefined
		: arrowY == null
			? "translateY(-50%)"
			: undefined;

	const positionStyle: CSSProperties = {
		position: "absolute",
		width: boxWidthPx,
		height: boxHeightPx,
		pointerEvents: "none",
		zIndex: 0,
		transform: [hangTransform, alignAxisTransform].filter(Boolean).join(" "),
		// Always set both axes so staticSide flips don't leave stale edge props.
		top: isVertical ? (pointsOutUp ? 0 : "auto") : arrowY != null ? arrowY : "50%",
		bottom: isVertical ? (pointsOutUp ? "auto" : 0) : "auto",
		left: isVertical
			? arrowX != null
				? arrowX
				: "50%"
			: pointsOutLeft
				? 0
				: "auto",
		right: isVertical ? "auto" : pointsOutLeft ? "auto" : 0,
	};

	/** Tip points outward from staticSide (toward the reference). */
	const outerTriangleStyle = buildCssTriangleStyle({
		isVertical,
		pointsOutUp,
		pointsOutLeft,
		halfWidthPx: geo.outerHalfWidthPx,
		heightPx: geo.outerHeightPx,
		tipColor: fills.borderFill,
		zIndex: 0,
	});
	const innerTriangleStyle = buildCssTriangleStyle({
		isVertical,
		pointsOutUp,
		pointsOutLeft,
		halfWidthPx: geo.innerHalfWidthPx,
		heightPx: geo.innerHeightPx,
		tipColor: fills.faceFill,
		zIndex: 1,
	});

	/**
	 * Border rim peek: shift mid+face slightly toward the panel so the larger
	 * border triangle shows as a hairline around the face tip (SpeechBubble /
	 * BlockQuote `borderInnerExtraDownPx`). Kept small so it does not inset.
	 */
	const rimShiftPx = 1;
	const midFillStyle: CSSProperties = {
		position: "absolute",
		...(isVertical
			? {
					left: "50%",
					width: geo.outerHalfWidthPx * 2,
					height: geo.outerHeightPx,
					transform: `translateX(-50%) translateY(${
						pointsOutUp ? rimShiftPx : -rimShiftPx
					}px)`,
					...(pointsOutUp ? { bottom: 0 } : { top: 0 }),
				}
			: {
					top: "50%",
					width: geo.outerHeightPx,
					height: geo.outerHalfWidthPx * 2,
					transform: `translateY(-50%) translateX(${
						pointsOutLeft ? rimShiftPx : -rimShiftPx
					}px)`,
					...(pointsOutLeft ? { right: 0 } : { left: 0 }),
				}),
	};

	return (
		<div
			ref={arrowRef}
			data-content-tooltip-arrow=""
			data-content-tooltip-arrow-side={staticSide}
			aria-hidden
			className={cn(
				colorScheme === "system" &&
					CONTENT_TOOLTIP_SYSTEM_ARROW_VAR_CLASS,
				colorScheme === "invert" &&
					CONTENT_TOOLTIP_INVERT_ARROW_VAR_CLASS,
			)}
			style={positionStyle}
		>
			<span
				style={{
					position: "relative",
					display: "block",
					width: "100%",
					height: "100%",
				}}
			>
				<span style={midFillStyle}>
					<span
						data-content-tooltip-arrow-border=""
						style={outerTriangleStyle}
					/>
					<span
						data-content-tooltip-arrow-face=""
						style={innerTriangleStyle}
					/>
				</span>
			</span>
		</div>
	);
}

/**
 * CSS border-triangle — always set longhand width/color on all four sides so
 * React 19 does not warn when staticSide flips (shorthand `borderColor` +
 * longhand tip color conflict).
 */
function buildCssTriangleStyle(params: {
	isVertical: boolean;
	pointsOutUp: boolean;
	pointsOutLeft: boolean;
	halfWidthPx: number;
	heightPx: number;
	tipColor: string;
	zIndex: number;
}): CSSProperties {
	const {
		isVertical,
		pointsOutUp,
		pointsOutLeft,
		halfWidthPx,
		heightPx,
		tipColor,
		zIndex,
	} = params;

	const base: CSSProperties = {
		position: "absolute",
		width: 0,
		height: 0,
		borderStyle: "solid",
		borderTopWidth: 0,
		borderRightWidth: 0,
		borderBottomWidth: 0,
		borderLeftWidth: 0,
		borderTopColor: "transparent",
		borderRightColor: "transparent",
		borderBottomColor: "transparent",
		borderLeftColor: "transparent",
		zIndex,
	};

	if (isVertical) {
		return {
			...base,
			left: "50%",
			right: "auto",
			top: pointsOutUp ? "auto" : 0,
			bottom: pointsOutUp ? 0 : "auto",
			transform: "translateX(-50%)",
			borderLeftWidth: halfWidthPx,
			borderRightWidth: halfWidthPx,
			...(pointsOutUp
				? {
						borderBottomWidth: heightPx,
						borderBottomColor: tipColor,
					}
				: {
						borderTopWidth: heightPx,
						borderTopColor: tipColor,
					}),
		};
	}

	return {
		...base,
		top: "50%",
		bottom: "auto",
		left: pointsOutLeft ? "auto" : 0,
		right: pointsOutLeft ? 0 : "auto",
		transform: "translateY(-50%)",
		borderTopWidth: halfWidthPx,
		borderBottomWidth: halfWidthPx,
		...(pointsOutLeft
			? {
					borderRightWidth: heightPx,
					borderRightColor: tipColor,
				}
			: {
					borderLeftWidth: heightPx,
					borderLeftColor: tipColor,
				}),
	};
}
